import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Paper,
  Button,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  ArrowBackIosNew,
  Campaign,
} from "@mui/icons-material";
import { auth, db, functions } from "./firebase";
import { httpsCallable } from "firebase/functions";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import { PayPalButtons, PayPalScriptProvider } from "@paypal/react-paypal-js";

// PayPal SDK - agregar a index.html: <script src="https://www.paypal.com/sdk?client-id=YOUR_CLIENT_ID"></script>

const PAISES = [
  "Afganistan", "Albania", "Alemania", "Andorra", "Angola", "Arabia Saudita", "Argelia", "Argentina",
  "Armenia", "Australia", "Austria", "Azerbaiyan", "Bahamas", "Banglades", "Barbados", "Belgica",
  "Belice", "Benin", "Bielorrusia", "Bolivia", "Bosnia y Herzegovina", "Botsuana", "Brasil", "Brunei",
  "Bulgaria", "Burkina Faso", "Burundi", "Cabo Verde", "Camboya", "Camerun", "Canada", "Catar",
  "Chad", "Chile", "China", "Chipre", "Colombia", "Comoras", "Corea del Sur", "Costa Rica", "Costa de Marfil",
  "Croacia", "Cuba", "Dinamarca", "Ecuador", "Egipto", "El Salvador", "Emiratos Arabes Unidos", "Eritrea",
  "Eslovaquia", "Eslovenia", "Espana", "Estados Unidos", "Estonia", "Etiopia", "Filipinas", "Finlandia",
  "Francia", "Gabon", "Gambia", "Georgia", "Ghana", "Grecia", "Guatemala", "Guinea", "Guinea Ecuatorial",
  "Guinea-Bisau", "Guyana", "Haiti", "Honduras", "Hungria", "India", "Indonesia", "Irak", "Iran", "Irlanda",
  "Islandia", "Israel", "Italia", "Jamaica", "Japon", "Jordania", "Kenia", "Kuwait", "Laos", "Letonia",
  "Libano", "Liberia", "Libia", "Lituania", "Luxemburgo", "Macedonia del Norte", "Madagascar", "Malasia",
  "Malaui", "Mali", "Malta", "Marruecos", "Mauricio", "Mauritania", "Mexico", "Moldavia", "Mongolia",
  "Montenegro", "Mozambique", "Myanmar", "Namibia", "Nepal", "Nicaragua", "Niger", "Nigeria", "Noruega",
  "Nueva Zelanda", "Oman", "Paises Bajos", "Pakistan", "Panama", "Paraguay", "Peru", "Polonia", "Portugal",
  "Puerto Rico", "Reino Unido", "Republica Checa", "Republica Dominicana", "Ruanda", "Rumania", "Rusia",
  "Senegal", "Serbia", "Singapur", "Siria", "Somalia", "Sri Lanka", "Sudafrica", "Sudan", "Suecia", "Suiza",
  "Surinam", "Tailandia", "Tanzania", "Togo", "Trinidad y Tobago", "Tunez", "Turquia", "Ucrania", "Uganda",
  "Uruguay", "Venezuela", "Vietnam", "Yemen", "Yibuti", "Zambia", "Zimbabue"
];

const PROMO_TEXT = {
  es: {
    unknown: 'Desconocido',
    cancelled: 'Cancelado',
    active: 'Activo',
    pendingPayment: 'Pendiente de pago',
    promotePost: 'Promocionar publicación',
    promoteProfile: 'Promocionar perfil',
    externalLink: 'Enlace externo',
    selectPost: 'Selecciona una publicación',
    enterUrl: 'Ingresa una URL válida',
    enterTitle: 'Ingresa un título para el anuncio',
    selectCountry: 'Selecciona al menos un país',
    adUpdated: 'Anuncio actualizado. Revisa y paga.',
    adReadyToPay: 'Anuncio listo para pagar. Se publicará al confirmar el pago.',
    adSaveError: 'Error al guardar el anuncio',
    paymentConfirmed: 'Pago confirmado correctamente. El anuncio fue publicado.',
    paymentConfirmError: 'No se pudo confirmar el pago.',
    adCancelled: 'Anuncio cancelado. No hay reembolso.',
    adCancelError: 'No se pudo cancelar el anuncio.',
    adDeleted: 'Anuncio eliminado correctamente.',
    adDeleteError: 'No se pudo eliminar el anuncio.',
    step: 'Paso',
    whatPromote: '¿Qué deseas promocionar?',
    promotionStatus: 'Estado de la promoción',
    noPromotions: 'Aún no tienes promociones creadas.',
    untitled: 'Sin título',
    impressions: 'Impresiones',
    clicks: 'Clicks',
    edit: 'Editar',
    viewStats: 'Ver estadísticas',
    delete: 'Eliminar',
    configureAd: 'Configura tu anuncio',
    editAd: 'Editar anuncio',
    websiteUrl: 'URL del sitio web',
    adTitle: 'Título del anuncio',
    adTitlePlaceholder: 'Ej: Descubre mi perfil',
    duration: 'Duración',
    days: 'días',
    budget: 'Presupuesto',
    back: 'Atrás',
    next: 'Siguiente',
    targetAudience: 'Público Objetivo',
    ageRange: 'Rango de edad',
    minAge: 'Edad mín.',
    maxAge: 'Edad máx.',
    countries: 'Países',
    countriesSelected: 'seleccionados',
    selectCountries: 'Seleccionar países',
    selectOneOrMore: 'Selecciona uno o más países',
    reviewChanges: 'Revisar cambios del anuncio',
    adSummary: 'Resumen del Anuncio',
    totalBudget: 'Presupuesto Total',
    saveChanges: 'Guardar cambios',
    createAd: 'Crear Anuncio',
    paypalNote: 'Nota: El pago se procesará en el siguiente paso a través de PayPal',
    adPayment: 'Pago del Anuncio',
    adId: 'ID de anuncio',
    totalToPay: 'Total a pagar',
    payToPublish: 'Tu anuncio se publicará únicamente cuando PayPal confirme el pago.',
    paypalMissing: 'Falta configurar REACT_APP_PAYPAL_CLIENT_ID para mostrar el botón de pago.',
    paypalError: 'Error al procesar pago con PayPal.',
    paymentDone: 'Pago completado. Tu anuncio ya está activo.',
    noRefund: 'Si cancelas el anuncio después del pago, no hay reembolso.',
    cancelAd: 'Cancelar anuncio',
    goBack: 'Volver',
    finish: 'Finalizar',
    finishPayFirst: 'Finalizar (paga primero)',
    confirmCancel: 'Confirmar cancelación',
    promoStats: 'Estadísticas de la promoción',
    status: 'Estado',
    ctr: 'CTR',
    targetCountries: 'Países objetivo',
    undefined: 'Sin definir',
    close: 'Cerrar',
    deleteAd: 'Eliminar anuncio',
    deleteConfirm: 'Esta acción eliminará el anuncio permanentemente. ¿Deseas continuar?',
    cancelWarning: 'Vas a cancelar este anuncio. Importante: no hay reembolso si ya realizaste el pago.',
    cancel: 'Cancelar',
    type: 'Tipo',
    post: 'Publicación',
    profile: 'Perfil',
    website: 'Sitio Web',
    years: 'años',
  },
  en: {
    unknown: 'Unknown',
    cancelled: 'Cancelled',
    active: 'Active',
    pendingPayment: 'Payment pending',
    promotePost: 'Promote post',
    promoteProfile: 'Promote profile',
    externalLink: 'External link',
    selectPost: 'Select a post',
    enterUrl: 'Enter a valid URL',
    enterTitle: 'Enter an ad title',
    selectCountry: 'Select at least one country',
    adUpdated: 'Ad updated. Review and pay.',
    adReadyToPay: 'Ad ready for payment. It will be published after payment confirmation.',
    adSaveError: 'Error saving ad',
    paymentConfirmed: 'Payment confirmed. Ad has been published.',
    paymentConfirmError: 'Could not confirm payment.',
    adCancelled: 'Ad cancelled. No refund.',
    adCancelError: 'Could not cancel ad.',
    adDeleted: 'Ad deleted successfully.',
    adDeleteError: 'Could not delete ad.',
    step: 'Step',
    whatPromote: 'What do you want to promote?',
    promotionStatus: 'Promotion status',
    noPromotions: 'You do not have promotions yet.',
    untitled: 'Untitled',
    impressions: 'Impressions',
    clicks: 'Clicks',
    edit: 'Edit',
    viewStats: 'View stats',
    delete: 'Delete',
    configureAd: 'Configure your ad',
    editAd: 'Edit ad',
    websiteUrl: 'Website URL',
    adTitle: 'Ad title',
    adTitlePlaceholder: 'Ex: Discover my profile',
    duration: 'Duration',
    days: 'days',
    budget: 'Budget',
    back: 'Back',
    next: 'Next',
    targetAudience: 'Target audience',
    ageRange: 'Age range',
    minAge: 'Min age',
    maxAge: 'Max age',
    countries: 'Countries',
    countriesSelected: 'selected',
    selectCountries: 'Select countries',
    selectOneOrMore: 'Select one or more countries',
    reviewChanges: 'Review ad changes',
    adSummary: 'Ad summary',
    totalBudget: 'Total budget',
    saveChanges: 'Save changes',
    createAd: 'Create ad',
    paypalNote: 'Note: Payment will be processed in the next step through PayPal',
    adPayment: 'Ad payment',
    adId: 'Ad ID',
    totalToPay: 'Total to pay',
    payToPublish: 'Your ad will be published only after PayPal confirms payment.',
    paypalMissing: 'REACT_APP_PAYPAL_CLIENT_ID is missing to render the PayPal button.',
    paypalError: 'Error processing PayPal payment.',
    paymentDone: 'Payment completed. Your ad is now active.',
    noRefund: 'If you cancel the ad after payment, there is no refund.',
    cancelAd: 'Cancel ad',
    goBack: 'Go back',
    finish: 'Finish',
    finishPayFirst: 'Finish (pay first)',
    confirmCancel: 'Confirm cancellation',
    promoStats: 'Promotion stats',
    status: 'Status',
    ctr: 'CTR',
    targetCountries: 'Target countries',
    undefined: 'Undefined',
    close: 'Close',
    deleteAd: 'Delete ad',
    deleteConfirm: 'This action will permanently delete the ad. Do you want to continue?',
    cancelWarning: 'You are about to cancel this ad. Important: there is no refund if payment was completed.',
    cancel: 'Cancel',
    type: 'Type',
    post: 'Post',
    profile: 'Profile',
    website: 'Website',
    years: 'years',
  },
};

const Promocionar = ({ onBack, language = 'es', themeMode = 'oscuro' }) => {
  const navigate = useNavigate();
  const user = auth.currentUser;
  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID || "";
  const t = useTranslatedDictionary(PROMO_TEXT, language);
  const isLightTheme = themeMode === 'claro';
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const dialogBg = isLightTheme ? "#ffffff" : "#0f141a";
  const adOptions = [
    { id: 'post', label: t('promotePost'), icon: '📱' },
    { id: 'profile', label: t('promoteProfile'), icon: '👤' },
    { id: 'website', label: t('externalLink'), icon: '🌐' },
  ];

  // Estados
  const [step, setStep] = useState(1); // 1: Tipo, 2: Config, 3: Publico, 4: Resumen, 5: Pago
  const [adType, setAdType] = useState(null);
  const [myPosts, setMyPosts] = useState([]);
  const [myAds, setMyAds] = useState([]);
  const [selectedPost, setSelectedPost] = useState(null);
  const [externalUrl, setExternalUrl] = useState('');
  const [adTitle, setAdTitle] = useState('');
  const [durationDays, setDurationDays] = useState(7);
  const [budget, setBudget] = useState(50);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [ageMin, setAgeMin] = useState(18);
  const [ageMax, setAgeMax] = useState(65);
  const [loading, setLoading] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [isEditingAd, setIsEditingAd] = useState(false);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openStatsDialog, setOpenStatsDialog] = useState(false);
  const [selectedAd, setSelectedAd] = useState(null);
  const [pendingAdData, setPendingAdData] = useState(null);
  const [createdAdId, setCreatedAdId] = useState("");
  const [toast, setToast] = useState({ open: false, type: 'success', text: '' });

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(-1);
  };

  // Cargar posts del usuario
  useEffect(() => {
    if (!user) return;
    const postsQuery = query(
      collection(db, 'posts'),
      where('uid', '==', user.uid)
    );
    const unsubscribe = onSnapshot(postsQuery, (snapshot) => {
      const posts = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));
      setMyPosts(posts);
    });
    return () => unsubscribe();
  }, [user]);

  // Cargar promociones del usuario
  useEffect(() => {
    if (!user) return;
    const adsQuery = query(collection(db, 'ads'), where('ownerId', '==', user.uid));
    const unsubscribe = onSnapshot(adsQuery, (snapshot) => {
      const ads = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      ads.sort((a, b) => {
        const aTime = a?.createdAt?.seconds || 0;
        const bTime = b?.createdAt?.seconds || 0;
        return bTime - aTime;
      });
      setMyAds(ads);
    });
    return () => unsubscribe();
  }, [user]);

  const getPromotionStatus = (ad) => {
    if (!ad) return t('unknown');
    if (ad.paymentStatus === "cancelled_no_refund" || ad.paymentStatus === "cancelled" || ad.active === false) {
      return t('cancelled');
    }
    if (ad.paymentStatus === "paid") return t('active');
    return t('pendingPayment');
  };

  const getPromotionStatusColor = (ad) => {
    if (!ad) return "#ffb300";
    if (ad.paymentStatus === "cancelled_no_refund" || ad.paymentStatus === "cancelled" || ad.active === false) {
      return "#ef5350";
    }
    if (ad.paymentStatus === "paid") return "#4caf50";
    return "#ffb300";
  };

  const getCtr = (ad) => {
    const impressions = Number(ad?.impressions || 0);
    const clicks = Number(ad?.clicks || 0);
    if (impressions <= 0) return "0.00";
    return ((clicks / impressions) * 100).toFixed(2);
  };

  const handleEditExistingAd = (ad) => {
    if (!ad) return;
    setSelectedAd(ad);
    setCreatedAdId(ad.id);
    setAdType(ad.adType || null);
    setSelectedPost(ad.postId || null);
    setExternalUrl(ad.externalUrl || '');
    setAdTitle(ad.title || '');
    setDurationDays(Number(ad.durationDays || 7));
    setBudget(Number(ad.budget || 50));
    setSelectedCountries(Array.isArray(ad.targetCountries) ? ad.targetCountries : []);
    setAgeMin(Number(ad.targetAgeMin || 18));
    setAgeMax(Number(ad.targetAgeMax || 65));
    setPaymentCompleted(ad.paymentStatus === 'paid');
    setIsEditingAd(true);
    setStep(2);
  };

  const handleOpenStats = (ad) => {
    setSelectedAd(ad);
    setOpenStatsDialog(true);
  };

  const handleCreateOrUpdateAd = async () => {
    if (!user) return;

    // Validaciones
    if (adType === 'post' && !selectedPost) {
      setToast({ open: true, type: 'error', text: t('selectPost') });
      return;
    }
    if (adType === 'website' && !externalUrl) {
      setToast({ open: true, type: 'error', text: t('enterUrl') });
      return;
    }
    if (!adTitle.trim()) {
      setToast({ open: true, type: 'error', text: t('enterTitle') });
      return;
    }
    if (selectedCountries.length === 0) {
      setToast({ open: true, type: 'error', text: t('selectCountry') });
      return;
    }

    setLoading(true);
    try {
      const post = adType === 'post' ? myPosts.find(p => p.id === selectedPost) : null;
      const normalizedPostMedia = post
        ? (Array.isArray(post.media)
            ? post.media
            : [
                ...(post.imageUrl ? [{ type: 'image', url: post.imageUrl }] : []),
                ...(post.videoUrl ? [{ type: 'video', url: post.videoUrl }] : []),
              ])
        : [];
      const primaryPostImage =
        normalizedPostMedia.find((item) => item?.type === 'image')?.url ||
        post?.imageUrl ||
        null;
      const primaryPostVideo =
        normalizedPostMedia.find((item) => item?.type === 'video')?.url ||
        post?.videoUrl ||
        null;
      const adPayload = {
        ownerId: user.uid,
        ownerName: user.displayName || user.email,
        ownerPhoto: user.photoURL,
        adType,
        title: adTitle,
        postId: adType === 'post' ? selectedPost : null,
        postText: post?.text || '',
        postImage: adType === 'post' ? primaryPostImage : null,
        postVideo: adType === 'post' ? primaryPostVideo : null,
        postMedia: adType === 'post' ? normalizedPostMedia : [],
        externalUrl: adType === 'website' ? externalUrl : null,
        targetCountries: selectedCountries,
        targetAgeMin: ageMin,
        targetAgeMax: ageMax,
        durationDays,
        budget,
        expiresAt: new Date(Date.now() + durationDays * 24 * 60 * 60 * 1000),
      };

      if (createdAdId) {
        // Un anuncio ya pagado no puede cambiar presupuesto ni duración
        // (las reglas de Firestore lo bloquean): solo se edita el contenido.
        const { durationDays: _d, budget: _b, expiresAt: _e, ...editablePayload } = adPayload;
        await updateDoc(doc(db, 'ads', createdAdId), {
          ...editablePayload,
          updatedAt: serverTimestamp(),
        });
        setToast({ open: true, type: 'success', text: t('adUpdated') });
      } else {
        setPendingAdData(adPayload);
        setToast({ open: true, type: 'success', text: t('adReadyToPay') });
      }

      setIsEditingAd(false);
      setStep(5);
    } catch (err) {
      setToast({ open: true, type: 'error', text: t('adSaveError') });
    } finally {
      setLoading(false);
    }
  };

  const handleApprovePayment = async (data) => {
    try {
      if (createdAdId) throw new Error("El anuncio ya fue pagado.");
      if (!pendingAdData) throw new Error("No se encontro anuncio para publicar.");

      // La captura del pago y la creación del anuncio se hacen en el
      // servidor, que verifica el monto contra PayPal antes de publicarlo.
      const captureAdPayment = httpsCallable(functions, "captureAdPayment");
      const { data: result } = await captureAdPayment({
        orderId: data?.orderID,
        ad: pendingAdData,
      });

      setPendingAdData(null);
      setCreatedAdId(result.adId);

      setPaymentCompleted(true);
      setToast({ open: true, type: "success", text: t('paymentConfirmed') });
    } catch (error) {
      setToast({ open: true, type: "error", text: t('paymentConfirmError') });
    }
  };

  const handleCancelAd = async () => {
    if (!createdAdId) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, "ads", createdAdId), {
        active: false,
        paymentStatus: paymentCompleted ? "cancelled_no_refund" : "cancelled",
        cancelledAt: serverTimestamp(),
      });
      setOpenCancelDialog(false);
      setToast({ open: true, type: "success", text: t('adCancelled') });
      setTimeout(() => handleBack(), 1000);
    } catch (error) {
      setToast({ open: true, type: "error", text: t('adCancelError') });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAd = async () => {
    if (!selectedAd?.id) return;
    setLoading(true);
    try {
      await deleteDoc(doc(db, "ads", selectedAd.id));
      if (createdAdId === selectedAd.id) {
        setCreatedAdId("");
        setPaymentCompleted(false);
        setIsEditingAd(false);
        setStep(1);
      }
      setOpenDeleteDialog(false);
      setToast({ open: true, type: "success", text: t('adDeleted') });
    } catch (error) {
      setToast({ open: true, type: "error", text: t('adDeleteError') });
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        color: onSurface(1),
        background: isLightTheme ? "linear-gradient(to bottom, #ffffff, #f2f5f8)" : "linear-gradient(to bottom, #101010, #000)",
        p: { xs: 2, sm: 3 },
        pb: 10,
      }}
    >
      <Box sx={{ maxWidth: "600px", mx: "auto" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
            position: "sticky",
            top: 0,
            zIndex: 10,
            py: 1,
            backgroundColor: isLightTheme ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.72)",
            backdropFilter: "blur(10px)",
            borderRadius: "12px",
          }}
        >
          <IconButton onClick={handleBack} sx={{ color: onSurface(1) }}>
            <ArrowBackIosNew fontSize="small" />
          </IconButton>
          <Stack direction="row" spacing={1} alignItems="center">
            <Campaign sx={{ color: "#00d4ff" }} />
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {`Gexvi Ads - ${t('step')} ${step}/5`}
            </Typography>
          </Stack>
        </Box>

        {/* Paso 1: Tipo de Anuncio */}
        {step === 1 && (
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: "18px",
              bgcolor: onSurface(0.04),
              border: `1px solid ${onSurface(0.1)}`,
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: onSurface(1) }}>
              {t('whatPromote')}
            </Typography>

            <Paper
              sx={{
                p: 2,
                mb: 2,
                borderRadius: "14px",
                bgcolor: onSurface(0.03),
                border: `1px solid ${onSurface(0.1)}`,
              }}
            >
              <Typography sx={{ color: onSurface(1), fontWeight: 700, mb: 1.2 }}>
                {t('promotionStatus')}
              </Typography>
              {myAds.length === 0 ? (
                <Typography sx={{ color: onSurface(0.65) }}>
                  {t('noPromotions')}
                </Typography>
              ) : (
                <Stack spacing={1.2}>
                  {myAds.slice(0, 4).map((ad) => (
                    <Paper
                      key={ad.id}
                      sx={{
                        p: 1.4,
                        borderRadius: "10px",
                        bgcolor: isLightTheme ? onSurface(0.05) : "rgba(0,0,0,0.35)",
                        border: `1px solid ${onSurface(0.12)}`,
                      }}
                    >
                      <Stack spacing={0.8}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Typography sx={{ color: onSurface(1), fontWeight: 700 }}>
                            {ad.title || t('untitled')}
                          </Typography>
                          <Typography sx={{ color: getPromotionStatusColor(ad), fontWeight: 700, fontSize: "0.82rem" }}>
                            {getPromotionStatus(ad)}
                          </Typography>
                        </Stack>
                        <Typography sx={{ color: onSurface(0.7), fontSize: "0.82rem" }}>
                          {t('impressions')}: {Number(ad.impressions || 0)} | {t('clicks')}: {Number(ad.clicks || 0)} | {t('ctr')}: {getCtr(ad)}%
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEditExistingAd(ad)}
                            sx={{ color: "#00d4ff", borderColor: "#00d4ff", textTransform: "none" }}
                          >
                            {t('edit')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleOpenStats(ad)}
                            sx={{ color: "#8ab4ff", borderColor: "#8ab4ff", textTransform: "none" }}
                          >
                            {t('viewStats')}
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              setSelectedAd(ad);
                              setOpenDeleteDialog(true);
                            }}
                            sx={{ textTransform: "none" }}
                          >
                            {t('delete')}
                          </Button>
                        </Stack>
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              )}
            </Paper>

            <Stack spacing={1.5}>
              {adOptions.map(option => (
                <Paper
                  key={option.id}
                  onClick={() => {
                    setAdType(option.id);
                    setStep(2);
                  }}
                  sx={{
                    p: 2,
                    bgcolor: adType === option.id ? "rgba(0,212,255,0.2)" : onSurface(0.04),
                    border: `2px solid ${adType === option.id ? "#00d4ff" : onSurface(0.1)}`,
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      bgcolor: "rgba(0,212,255,0.15)",
                      borderColor: "#00d4ff",
                    }
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Typography sx={{ fontSize: '2rem' }}>{option.icon}</Typography>
                    <Typography sx={{ color: onSurface(1), fontWeight: 600 }}>
                      {option.label}
                    </Typography>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Paper>
        )}

        {/* Paso 2: Configuración */}
        {step === 2 && (
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: "18px",
              bgcolor: onSurface(0.04),
              border: `1px solid ${onSurface(0.1)}`,
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: onSurface(1) }}>
              {isEditingAd ? t('editAd') : t('configureAd')}
            </Typography>
            <Stack spacing={2}>
              {/* Seleccionar publicación si es tipo 'post' */}
              {adType === 'post' && (
                <Box>
                  <Typography sx={{ color: onSurface(0.8), mb: 1 }}>
                    {t('selectPost')}
                  </Typography>
                  <Select
                    value={selectedPost || ''}
                    onChange={(e) => setSelectedPost(e.target.value)}
                    sx={{
                      width: '100%',
                      bgcolor: isLightTheme ? '#f2f5f8' : '#151c24',
                      color: onSurface(1),
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: onSurface(0.1),
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#00d4ff',
                      },
                    }}
                  >
                    {myPosts.map(post => (
                      <MenuItem key={post.id} value={post.id}>
                        {post.text.substring(0, 50)}...
                      </MenuItem>
                    ))}
                  </Select>
                </Box>
              )}

              {/* URL externa si es tipo 'website' */}
              {adType === 'website' && (
                <TextField
                  label={t('websiteUrl')}
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  placeholder="https://ejemplo.com"
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: onSurface(1),
                      '& fieldset': { borderColor: onSurface(0.2) },
                      '&:hover fieldset': { borderColor: '#00d4ff' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                    },
                    '& .MuiInputBase-input::placeholder': { color: onSurface(0.6) },
                    '& .MuiInputLabel-root': { color: onSurface(0.7) },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#00d4ff' },
                  }}
                />
              )}

              {/* Título del anuncio */}
              <TextField
                label={t('adTitle')}
                value={adTitle}
                onChange={(e) => setAdTitle(e.target.value)}
                placeholder={t('adTitlePlaceholder')}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: onSurface(1),
                    '& fieldset': { borderColor: onSurface(0.2) },
                    '&:hover fieldset': { borderColor: '#00d4ff' },
                    '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                  },
                  '& .MuiInputBase-input::placeholder': { color: onSurface(0.6) },
                  '& .MuiInputLabel-root': { color: onSurface(0.7) },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#00d4ff' },
                }}
              />

              {/* Duración */}
              <Box>
                <Typography sx={{ color: onSurface(0.8), mb: 1 }}>
                  {t('duration')}: {durationDays} {t('days')}
                </Typography>
                <input
                  type="range"
                  min="1"
                  max="30"
                  value={durationDays}
                  onChange={(e) => setDurationDays(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
              </Box>

              {/* Presupuesto */}
              <Box>
                <Typography sx={{ color: onSurface(0.8), mb: 1 }}>
                  {t('budget')}: ${budget}
                </Typography>
                <input
                  type="range"
                  min="10"
                  max="1000"
                  step="10"
                  value={budget}
                  onChange={(e) => setBudget(Number(e.target.value))}
                  style={{ width: '100%' }}
                />
              </Box>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setStep(1)}
                  sx={{ flex: 1, color: '#00d4ff', borderColor: '#00d4ff' }}
                >
                  {t('back')}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setStep(3)}
                  sx={{ flex: 1, bgcolor: '#00d4ff', color: '#001217' }}
                >
                  {t('next')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Paso 3: Público Objetivo */}
        {step === 3 && (
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: "18px",
              bgcolor: onSurface(0.04),
              border: `1px solid ${onSurface(0.1)}`,
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: onSurface(1) }}>
              {t('targetAudience')}
            </Typography>
            <Stack spacing={2}>
              {/* Rango de edad */}
              <Box>
                <Typography sx={{ color: onSurface(0.8), mb: 1 }}>
                  {t('ageRange')}: {ageMin} - {ageMax} {t('years')}
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  <TextField
                    type="number"
                    label={t('minAge')}
                    value={ageMin}
                    onChange={(e) => setAgeMin(Number(e.target.value))}
                    inputProps={{ min: 13, max: 100 }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        color: onSurface(1),
                        '& fieldset': { borderColor: onSurface(0.2) },
                        '&:hover fieldset': { borderColor: '#00d4ff' },
                        '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                      },
                      '& .MuiInputLabel-root': { color: onSurface(0.7) },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#00d4ff' },
                    }}
                  />
                  <TextField
                    type="number"
                    label={t('maxAge')}
                    value={ageMax}
                    onChange={(e) => setAgeMax(Number(e.target.value))}
                    inputProps={{ min: 13, max: 100 }}
                    sx={{
                      flex: 1,
                      '& .MuiOutlinedInput-root': {
                        color: onSurface(1),
                        '& fieldset': { borderColor: onSurface(0.2) },
                        '&:hover fieldset': { borderColor: '#00d4ff' },
                        '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                      },
                      '& .MuiInputLabel-root': { color: onSurface(0.7) },
                      '& .MuiInputLabel-root.Mui-focused': { color: '#00d4ff' },
                    }}
                  />
                </Stack>
              </Box>

              {/* Seleccionar países */}
              <Box>
                <Typography sx={{ color: onSurface(0.8), mb: 1 }}>
                  {t('countries')} ({selectedCountries.length} {t('countriesSelected')})
                </Typography>
                <TextField
                  select
                  fullWidth
                  label={t('selectCountries')}
                  value={selectedCountries}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedCountries(typeof value === 'string' ? value.split(',') : value);
                  }}
                  SelectProps={{
                    multiple: true,
                    displayEmpty: true,
                    renderValue: (selected) =>
                      selected.length === 0
                        ? t('selectOneOrMore')
                        : selected.join(', '),
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          maxHeight: 320,
                          bgcolor: '#101820',
                          color: onSurface(1),
                        },
                      },
                    },
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      color: onSurface(1),
                      '& fieldset': { borderColor: onSurface(0.2) },
                      '&:hover fieldset': { borderColor: '#00d4ff' },
                      '&.Mui-focused fieldset': { borderColor: '#00d4ff' },
                    },
                    '& .MuiInputLabel-root': { color: onSurface(0.7) },
                    '& .MuiInputLabel-root.Mui-focused': { color: '#00d4ff' },
                    '& .MuiSelect-icon': { color: onSurface(1) },
                  }}
                >
                  {PAISES.map((country) => (
                    <MenuItem key={country} value={country}>
                      {country}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setStep(2)}
                  sx={{ flex: 1, color: '#00d4ff', borderColor: '#00d4ff' }}
                >
                  {t('back')}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => setStep(4)}
                  sx={{ flex: 1, bgcolor: '#00d4ff', color: '#001217' }}
                >
                  {t('next')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {/* Paso 4: Resumen y Pago */}
        {step === 4 && (
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: "18px",
              bgcolor: onSurface(0.04),
              border: `1px solid ${onSurface(0.1)}`,
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: onSurface(1) }}>
              {isEditingAd ? t('reviewChanges') : t('adSummary')}
            </Typography>
            <Stack spacing={2} sx={{ mb: 3 }}>
              <Box sx={{ bgcolor: 'rgba(0,212,255,0.1)', p: 2, borderRadius: '10px' }}>
                <Typography sx={{ color: onSurface(0.7), fontSize: '0.85rem' }}>
                  {t('type')}: {adType === 'post' ? t('post') : adType === 'profile' ? t('profile') : t('website')}
                </Typography>
                <Typography sx={{ color: onSurface(1), fontWeight: 600 }}>{adTitle}</Typography>
              </Box>
              <Box sx={{ bgcolor: 'rgba(0,212,255,0.1)', p: 2, borderRadius: '10px' }}>
                <Typography sx={{ color: onSurface(0.7), fontSize: '0.85rem' }}>
                  {t('duration')}: {durationDays} {t('days')} | {t('targetAudience')}: {ageMin}-{ageMax} {t('years')}
                </Typography>
                <Typography sx={{ color: onSurface(0.7), fontSize: '0.85rem' }}>
                  {t('countries')}: {selectedCountries.join(', ')}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: 'rgba(0,212,255,0.3)', p: 2, borderRadius: '10px', border: '1px solid rgba(0,212,255,0.5)' }}>
                <Typography sx={{ color: '#00d4ff', fontSize: '0.9rem' }}>
                  {t('totalBudget')}
                </Typography>
                <Typography sx={{ color: onSurface(1), fontSize: '1.8rem', fontWeight: 800 }}>
                  ${budget.toFixed(2)}
                </Typography>
              </Box>
            </Stack>

            <Stack direction="row" spacing={2}>
              <Button
                variant="outlined"
                onClick={() => setStep(3)}
                sx={{ flex: 1, color: '#00d4ff', borderColor: '#00d4ff' }}
              >
                {t('back')}
              </Button>
              <Button
                variant="contained"
                onClick={handleCreateOrUpdateAd}
                disabled={loading}
                sx={{ flex: 1, bgcolor: '#00d4ff', color: '#001217' }}
              >
                {loading ? <CircularProgress size={24} /> : createdAdId ? t('saveChanges') : t('createAd')}
              </Button>
            </Stack>
            
            <Typography sx={{ color: onSurface(0.5), fontSize: '0.75rem', mt: 2, textAlign: 'center' }}>
              {t('paypalNote')}
            </Typography>
          </Paper>
        )}

        {/* Paso 5: Pago */}
        {step === 5 && (
          <Paper
            sx={{
              p: { xs: 2, sm: 3 },
              borderRadius: "18px",
              bgcolor: onSurface(0.04),
              border: `1px solid ${onSurface(0.1)}`,
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: onSurface(1) }}>
              {t('adPayment')}
            </Typography>

            <Stack spacing={2.2}>
              <Box sx={{ bgcolor: 'rgba(0,212,255,0.1)', p: 2, borderRadius: '10px' }}>
                <Typography sx={{ color: onSurface(0.7), fontSize: '0.85rem' }}>
                  {t('adId')}: {createdAdId || 'N/A'}
                </Typography>
                <Typography sx={{ color: onSurface(1), fontWeight: 700, fontSize: '1.25rem' }}>
                  {t('totalToPay')}: ${budget.toFixed(2)}
                </Typography>
                <Typography sx={{ color: onSurface(0.75), mt: 0.8, fontSize: '0.9rem' }}>
                  {t('payToPublish')}
                </Typography>
              </Box>

              {paymentCompleted || createdAdId ? null : !paypalClientId ? (
                <Alert severity="warning" sx={{ bgcolor: 'rgba(255,193,7,0.14)', color: onSurface(1) }}>
                  {t('paypalMissing')}
                </Alert>
              ) : (
                <Box sx={{ bgcolor: isLightTheme ? '#eef2f5' : '#111820', borderRadius: '12px', p: 1.5, border: `1px solid ${onSurface(0.08)}` }}>
                  <PayPalScriptProvider
                    options={{
                      clientId: paypalClientId,
                      currency: "USD",
                      intent: "capture",
                    }}
                  >
                    <PayPalButtons
                      style={{ layout: "vertical", shape: "pill", color: "black", label: "paypal", tagline: false }}
                      createOrder={(data, actions) =>
                        actions.order.create({
                          purchase_units: [
                            {
                              description: `Promocion de anuncio ${createdAdId}`,
                              amount: {
                                currency_code: "USD",
                                value: budget.toFixed(2),
                              },
                            },
                          ],
                        })
                      }
                      onApprove={handleApprovePayment}
                      onError={() => {
                        setToast({ open: true, type: "error", text: t('paypalError') });
                      }}
                    />
                  </PayPalScriptProvider>
                </Box>
              )}

              {paymentCompleted && (
                <Alert severity="success" sx={{ bgcolor: 'rgba(76,175,80,0.14)', color: onSurface(1) }}>
                  {t('paymentDone')}
                </Alert>
              )}

              <Alert severity="warning" sx={{ bgcolor: 'rgba(255,193,7,0.14)', color: onSurface(1) }}>
                {t('noRefund')}
              </Alert>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsEditingAd(true);
                    setStep(2);
                  }}
                  sx={{ flex: 1, color: '#00d4ff', borderColor: '#00d4ff' }}
                >
                  {t('editAd')}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => setOpenCancelDialog(true)}
                  sx={{ flex: 1 }}
                >
                  {t('cancelAd')}
                </Button>
              </Stack>

              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  onClick={() => setStep(4)}
                  sx={{ flex: 1, color: '#00d4ff', borderColor: '#00d4ff' }}
                >
                  {t('goBack')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleBack}
                  disabled={!paymentCompleted}
                  sx={{ flex: 1, bgcolor: '#00d4ff', color: '#001217' }}
                >
                  {paymentCompleted ? t('finish') : t('finishPayFirst')}
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
      >
        <Alert severity={toast.type}>{toast.text}</Alert>
      </Snackbar>

      <Dialog
        open={openCancelDialog}
        onClose={() => !loading && setOpenCancelDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            border: `1px solid ${onSurface(0.12)}`,
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{t('cancelAd')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: onSurface(0.85) }}>
            {t('cancelWarning')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setOpenCancelDialog(false)}
            disabled={loading}
            sx={{ color: onSurface(0.8), textTransform: "none" }}
          >
            {t('goBack')}
          </Button>
          <Button
            onClick={handleCancelAd}
            disabled={loading}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: onSurface(1) }} /> : t('confirmCancel')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openStatsDialog}
        onClose={() => setOpenStatsDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            border: `1px solid ${onSurface(0.12)}`,
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{t('promoStats')}</DialogTitle>
        <DialogContent>
          <Stack spacing={1.1}>
            <Typography sx={{ color: onSurface(1), fontWeight: 700 }}>
              {selectedAd?.title || t('untitled')}
            </Typography>
            <Typography sx={{ color: onSurface(0.78) }}>
              {t('status')}: <strong>{getPromotionStatus(selectedAd)}</strong>
            </Typography>
            <Typography sx={{ color: onSurface(0.78) }}>
              {t('impressions')}: {Number(selectedAd?.impressions || 0)}
            </Typography>
            <Typography sx={{ color: onSurface(0.78) }}>
              {t('clicks')}: {Number(selectedAd?.clicks || 0)}
            </Typography>
            <Typography sx={{ color: onSurface(0.78) }}>
              {t('ctr')}: {getCtr(selectedAd)}%
            </Typography>
            <Typography sx={{ color: onSurface(0.78) }}>
              {t('budget')}: ${Number(selectedAd?.budget || 0).toFixed(2)}
            </Typography>
            <Typography sx={{ color: onSurface(0.78) }}>
              {t('targetCountries')}: {Array.isArray(selectedAd?.targetCountries) && selectedAd.targetCountries.length > 0 ? selectedAd.targetCountries.join(', ') : t('undefined')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setOpenStatsDialog(false)} sx={{ color: "#00d4ff", textTransform: "none" }}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDeleteDialog}
        onClose={() => !loading && setOpenDeleteDialog(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            bgcolor: dialogBg,
            color: onSurface(1),
            border: `1px solid ${onSurface(0.12)}`,
            borderRadius: "12px",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>{t('deleteAd')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: onSurface(0.85) }}>
            {t('deleteConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button
            onClick={() => setOpenDeleteDialog(false)}
            disabled={loading}
            sx={{ color: onSurface(0.8), textTransform: "none" }}
          >
            {t('cancel')}
          </Button>
          <Button
            onClick={handleDeleteAd}
            disabled={loading}
            variant="contained"
            color="error"
            sx={{ textTransform: "none" }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: onSurface(1) }} /> : t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Promocionar;
